/**
 * BlockGuidance — what a block means, opened from an info button on hover,
 * focus or click: on the grid's cell headers and beside the checklist's block
 * badge — the segment, what it means, the action and the guidance behind it.
 * ReflectionQuestionsButton opens the HR process owner's reflection questions, once,
 * from the checklist header and the grid's toolbar. Content lives in
 * eduvosBlocks.ts.
 */
import { useRef, useState, type ReactNode } from 'react'
import { HelpCircle, Info } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { BLOCK_GUIDANCE, REFLECTION_QUESTIONS, ZONE_TEXT_CLASS, zoneOf } from './eduvosBlocks'

/** The four groups, as a compact list. */
export function ReflectionQuestionList({ className }: { className?: string }) {
  return (
    <div className={cn('space-y-3', className)}>
      {REFLECTION_QUESTIONS.map((g) => (
        <div key={g.title}>
          <p className="text-xs font-semibold text-gray-800">
            {g.title} <span className="font-normal text-gray-500">· {g.lead}</span>
          </p>
          <ul className="mt-1 list-disc space-y-1 pl-4 text-xs text-gray-700">
            {g.questions.map((q) => (
              <li key={q}>{q}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}

/** A popover that opens on hover, focus or click of its trigger. */
function HoverPopover({ trigger, children, width = 'w-96' }: { trigger: (bind: HoverBind) => ReactNode; children: ReactNode; width?: string }) {
  const [open, setOpen] = useState(false)
  const closeTimer = useRef<number | null>(null)
  const cancelClose = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }
  const scheduleClose = () => {
    cancelClose()
    closeTimer.current = window.setTimeout(() => setOpen(false), 150)
  }
  const contentRef = useRef<HTMLDivElement | null>(null)
  const bind: HoverBind = {
    onMouseEnter: () => {
      cancelClose()
      setOpen(true)
    },
    onMouseLeave: scheduleClose,
    onFocus: () => setOpen(true),
    // Focus moving into the popover keeps it open; anything else lets it close.
    onBlur: () => {
      requestAnimationFrame(() => {
        if (contentRef.current?.contains(document.activeElement)) return
        scheduleClose()
      })
    },
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger(bind)}</PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        align="start"
        sideOffset={6}
        collisionPadding={16}
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
        // Keep focus where it is: Radix would move it into the popover on
        // open and back to the trigger on close, and the trigger's own
        // focus/blur handlers would then open and close it in a loop.
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        // Never taller than the room Radix measures to the viewport edge; scrolls inside.
        className={cn('max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-0 text-left', width)}
      >
        {children}
      </PopoverContent>
    </Popover>
  )
}

interface HoverBind {
  onMouseEnter: () => void
  onMouseLeave: () => void
  onFocus: () => void
  onBlur: () => void
}

export default function BlockGuidance({ block, className }: { block: number; className?: string }) {
  const guidance = BLOCK_GUIDANCE[block]
  const zone = zoneOf(block)
  if (!guidance || !zone) return null
  return (
    <HoverPopover
      width="w-72"
      trigger={(bind) => (
        <button
          type="button"
          aria-label={`About block ${block}`}
          data-demo="talent-block-guidance"
          {...bind}
          className={cn(
            'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:text-heritage-blue',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-heritage-blue',
            className,
          )}
        >
          <Info className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
    >
      <div className="px-4 py-3">
        <p className={cn('flex flex-wrap items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide', ZONE_TEXT_CLASS[zone])}>
          <span>
            Block {block} · {guidance.name}
          </span>
          {guidance.hipo && (
            <span className="rounded bg-emerald-700 px-1.5 py-0.5 text-[9px] font-bold tracking-wide text-white">HIPO</span>
          )}
        </p>
        <p className="mt-1.5 text-xs text-gray-700">{guidance.summary}</p>
        <p className="mt-2 text-xs font-semibold italic text-gray-900">{guidance.action}</p>
        <p className="mt-1 text-xs text-gray-600">{guidance.detail}</p>
      </div>
    </HoverPopover>
  )
}

/** The whole list from one button, for the checklist header. */
export function ReflectionQuestionsButton({ className }: { className?: string }) {
  return (
    <HoverPopover
      trigger={(bind) => (
        <Button type="button" size="sm" className={cn('gap-1.5', className)} data-demo="talent-reflection-questions" {...bind}>
          <HelpCircle className="h-4 w-4" aria-hidden />
          Reflection questions
        </Button>
      )}
    >
      <div className="max-h-96 overflow-y-auto px-4 py-3">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          Once your team is on the grid, review each placement against these
        </p>
        <ReflectionQuestionList />
      </div>
    </HoverPopover>
  )
}
